import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Tab,
  Card,
  Tabs,
  Stack,
  Button,
  MenuItem,
  TextField,
  Container,
  Typography,
  LinearProgress,
} from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
// views
import QuestionAnalytics from './QuestionAnalytics';
import StudentAnswers from './StudentAnswers';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// utils
import axiosInstance from '../../utils/axios';
import { APIS } from '../../utils/serviceUrls';
import fmtDuration from '../../utils/formatDuration';
import { downloadCsv, slugForFile, CsvCell } from '../../utils/csv';

// ---------------------------------------------------------------------------
// Quiz Analysis — the ONE place to read program quiz results.
//
// Deliberately a single guided flow, Google-Forms style:
//   program -> batch -> quiz -> Summary | Individual responses
//
// It owns the selection; the two views are the existing components scoped to
// the chosen program + batch, so the numbers always match what you picked.
//
// Accepts ?eventId= & ?batchId= so the per-batch report can deep-link straight
// into the right cohort.
// ---------------------------------------------------------------------------

const ALL_BATCHES = '__all__';
const NO_BATCH = 'none';

export default function ProgramQuizAnalysis() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [programs, setPrograms] = useState<any[]>([]);
  const [eventId, setEventId] = useState(searchParams.get('eventId') || '');
  const [batchId, setBatchId] = useState(searchParams.get('batchId') || ALL_BATCHES);
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [quizId, setQuizId] = useState('');
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState<'summary' | 'individual'>('summary');

  // 1. Programs. Prefer the lightweight summary (no embedded lectures); fall
  // back to the full list for a backend that predates /events/summary.
  useEffect(() => {
    let cancelled = false;
    const asList = (res: any) => {
      const d = res.data?.data ?? res.data;
      return Array.isArray(d) ? d : d?.items || [];
    };
    (async () => {
      let list: any[] = [];
      try {
        list = asList(await axiosInstance.get(APIS.EVENTS.SUMMARY));
      } catch {
        try {
          list = asList(await axiosInstance.get(APIS.EVENTS.LIST));
        } catch (e: any) {
          if (!cancelled) enqueueSnackbar(e?.message || 'Failed to load programs', { variant: 'error' });
        }
      }
      if (cancelled) return;
      setPrograms(list);
      // Preselect the first program when nothing was deep-linked.
      setEventId((prev) => prev || (list[0]?._id ? String(list[0]._id) : ''));
      setLoadingPrograms(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const program = useMemo(
    () => programs.find((p) => String(p._id) === String(eventId)),
    [programs, eventId]
  );
  const batches = (program?.batches || []) as any[];

  // 2. Quizzes that actually have attempts in the chosen program + batch.
  useEffect(() => {
    if (!eventId) {
      setQuizzes([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingQuizzes(true);
    axiosInstance
      .get(APIS.QUIZ_ATTEMPTS.STATS, {
        params: {
          scope: 'program',
          eventId,
          batchId: batchId === ALL_BATCHES ? undefined : batchId,
        },
      })
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data ?? res.data;
        const list = Array.isArray(d) ? d : [];
        setQuizzes(list);
        // Keep the current quiz if it still exists in this scope, else pick the busiest.
        setQuizId((prev) => {
          if (prev && list.some((q: any) => q.quizId === prev)) return prev;
          return list[0]?.quizId || '';
        });
      })
      .catch(() => !cancelled && setQuizzes([]))
      .finally(() => !cancelled && setLoadingQuizzes(false));
    return () => {
      cancelled = true;
    };
  }, [eventId, batchId]);

  const quiz = useMemo(() => quizzes.find((q) => q.quizId === quizId), [quizzes, quizId]);

  const batchLabel =
    batchId === ALL_BATCHES
      ? 'All batches'
      : batchId === NO_BATCH
      ? 'Unassigned'
      : batches.find((b) => String(b._id) === String(batchId))?.name || 'Batch';

  // Export #2 — the full matrix: every student (rows) x every quiz in the
  // selection (columns), each cell the student's best score, plus how many
  // quizzes they completed (completion %) and an overall average. Built from
  // the per-student-per-quiz rollup so one file answers "who did what".
  const handleExportMatrix = async () => {
    if (!eventId || !quizzes.length) return;
    setExporting(true);
    try {
      const res = await axiosInstance.get(APIS.QUIZ_ATTEMPTS.BY_STUDENT, {
        params: {
          scope: 'program',
          eventId,
          batchId: batchId === ALL_BATCHES ? undefined : batchId,
        },
      });
      const d = res.data?.data ?? res.data;
      const list: any[] = Array.isArray(d) ? d : [];
      if (!list.length) {
        enqueueSnackbar('No quiz results in this selection yet', { variant: 'info' });
        return;
      }

      // Quiz columns follow the picker order; label by title (fallback: id).
      const quizCols = quizzes.map((q) => ({ id: q.quizId, title: q.quizTitle || q.quizId }));

      // Group the rollup rows by student.
      type Agg = {
        name: string;
        email: string;
        scores: Map<string, number>; // quizId -> best %
        passed: number;
      };
      const byUser = new Map<string, Agg>();
      list.forEach((r) => {
        const uid = String(r.userId);
        const agg =
          byUser.get(uid) ||
          { name: r.studentName || '', email: r.studentEmail || '', scores: new Map(), passed: 0 };
        const best = Number(r.bestPercentage ?? r.avgPercentage ?? 0);
        agg.scores.set(String(r.quizId), best);
        if (r.passed) agg.passed += 1;
        byUser.set(uid, agg);
      });

      const totalQuizzes = quizCols.length;
      const header: CsvCell[] = [
        'Student',
        'Email',
        ...quizCols.map((q) => q.title),
        'Quizzes taken',
        'Total quizzes',
        'Completion %',
        'Quizzes passed',
        'Overall avg %',
      ];
      const rowsOut: CsvCell[][] = Array.from(byUser.values())
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => {
          const cells: CsvCell[] = [s.name, s.email];
          let taken = 0;
          let sum = 0;
          quizCols.forEach((q) => {
            if (s.scores.has(q.id)) {
              const v = s.scores.get(q.id) as number;
              cells.push(v);
              taken += 1;
              sum += v;
            } else {
              cells.push('');
            }
          });
          cells.push(taken);
          cells.push(totalQuizzes);
          cells.push(totalQuizzes ? Math.round((taken / totalQuizzes) * 100) : 0);
          cells.push(s.passed);
          cells.push(taken ? Math.round(sum / taken) : 0);
          return cells;
        });

      const programName = program?.title || program?.name || 'program';
      const name = `program-${slugForFile(programName)}-${slugForFile(batchLabel)}-all-quizzes`;
      downloadCsv(name, [header, ...rowsOut]);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Export failed', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Page title="Quiz Analysis">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Quiz Analysis"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Programs', href: PATH_DASHBOARD.events },
            { name: 'Quiz Analysis' },
          ]}
          action={
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:download-outline" />}
                onClick={handleExportMatrix}
                disabled={!quizzes.length || exporting}
              >
                Export all students × quizzes
              </Button>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:arrow-back-outline" />}
                onClick={() => navigate(PATH_DASHBOARD.events)}
              >
                Back to programs
              </Button>
            </Stack>
          }
        />

        {(loadingPrograms || loadingQuizzes) && <LinearProgress sx={{ mb: 2 }} />}

        {/* Step 1-3: program -> batch -> quiz */}
        <Card sx={{ p: 2.5, mb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1.4fr' },
            }}
          >
            <TextField
              select
              size="small"
              label="1. Program"
              value={eventId}
              onChange={(e) => {
                setEventId(e.target.value);
                setBatchId(ALL_BATCHES);
                setQuizId('');
              }}
              disabled={!programs.length}
            >
              {programs.map((p) => (
                <MenuItem key={p._id} value={String(p._id)}>
                  {p.title || p.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="2. Batch"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              helperText={
                batches.length ? `${batches.length} batch(es)` : 'This program has no batches'
              }
            >
              <MenuItem value={ALL_BATCHES}>All batches</MenuItem>
              {batches.map((b) => (
                <MenuItem key={String(b._id)} value={String(b._id)}>
                  {b.name} ({(b.students || []).length} students)
                </MenuItem>
              ))}
              <MenuItem value={NO_BATCH}>Unassigned (no batch)</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="3. Quiz"
              value={quizId}
              onChange={(e) => setQuizId(e.target.value)}
              disabled={!quizzes.length}
              helperText={
                !loadingQuizzes && !quizzes.length
                  ? 'No quiz attempts in this selection yet'
                  : undefined
              }
            >
              {quizzes.map((q) => (
                <MenuItem key={q.quizId} value={q.quizId}>
                  {q.quizTitle || q.quizId}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {quiz && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              <Label color="info">{batchLabel}</Label>
              <Label color="default">{quiz.attempts} attempts</Label>
              <Label color="default">{quiz.students} students</Label>
              <Label color={quiz.avgPercentage >= 50 ? 'success' : 'warning'}>
                {quiz.avgPercentage}% average
              </Label>
              <Label color="default">{quiz.passRate}% pass</Label>
              {quiz.avgDurationSeconds ? (
                <Label color="default">{fmtDuration(quiz.avgDurationSeconds)} avg time</Label>
              ) : null}
            </Stack>
          )}
        </Card>

        {!quizId ? (
          <Card sx={{ p: 6, textAlign: 'center' }}>
            <Iconify
              icon="eva:clipboard-outline"
              sx={{ width: 44, height: 44, color: 'text.disabled', mb: 1 }}
            />
            <Typography variant="subtitle1">Nothing to analyse yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Pick a program and batch above. Results appear once students in that selection submit
              a quiz.
            </Typography>
          </Card>
        ) : (
          <Card>
            <Tabs
              value={view}
              onChange={(_e, v) => setView(v)}
              sx={{ px: 2, bgcolor: 'background.neutral' }}
            >
              <Tab
                value="summary"
                label="Summary"
                icon={<Iconify icon="eva:bar-chart-outline" width={18} height={18} />}
                iconPosition="start"
              />
              <Tab
                value="individual"
                label="Individual responses"
                icon={<Iconify icon="eva:people-outline" width={18} height={18} />}
                iconPosition="start"
              />
            </Tabs>

            <Box sx={{ p: 2.5 }}>
              {view === 'summary' ? (
                <QuestionAnalytics
                  quizId={quizId}
                  eventId={eventId}
                  batchId={batchId === ALL_BATCHES ? undefined : batchId}
                />
              ) : (
                <StudentAnswers
                  quizId={quizId}
                  eventId={eventId}
                  batchId={batchId === ALL_BATCHES ? undefined : batchId}
                  quizTitle={quiz?.quizTitle}
                  batchLabel={batchLabel}
                />
              )}
            </Box>
          </Card>
        )}
      </Container>
    </Page>
  );
}
